import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import defaultLogo from "../assets/logo.png";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { useToast } from "../hooks/useToast";
import { login } from "../services/authService";
import { setToken, setUser } from "../services/authSlice";
import { hasRole } from "../utils/roleHelpers";
import { cacheTenantBranding } from "../utils/tenantBrandingCache";
import stylesAuth from "./AuthPage.module.css";
import styles from "./Form.module.css";

interface LoginFormProps {
  onForgotPassword?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onForgotPassword }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();
  const { logo: brandedLogo, tenantName } = useTenantBranding();
  const logo = brandedLogo || defaultLogo;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = await login(email, password);
      if (data.error) {
        setError(data.error);
        showError(data.error);
      } else if (data.token) {
        dispatch(setToken(data.token));
        dispatch(setUser(data.user));
        showSuccess("Login successful!");

        // fetch and cache tenant branding for future use
        if (data.user.tenantSlug) {
          try {
            const brandingResponse = await fetch(
              `/api/tenants/public/${data.user.tenantSlug}`,
            );
            if (brandingResponse.ok) {
              const tenantData = await brandingResponse.json();
              if (tenantData.branding) {
                cacheTenantBranding(
                  data.user.tenantSlug,
                  tenantData.branding,
                  tenantData.name,
                );
              }
            }
          } catch (_error) {}
        }

        // redirect based on role and tenant
        if (
          hasRole(data.user.roles, "developer") ||
          hasRole(data.user.roles, "tenant-manager")
        ) {
          navigate("/tenant-manager/tenants");
        } else if (data.user.tenantSlug) {
          navigate(`/${data.user.tenantSlug}/orders`);
        } else {
          // fallback for users without tenant slug - redirect to auth to choose tenant
          navigate("/auth");
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      showError(message);
    }
  };

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: "0" }}>
        <img src={logo} alt="Company Logo" className={stylesAuth.logo} />
      </div>
      <h2 className={stylesAuth.companyName}>{tenantName || "Monulink"}</h2>
      <h3 className={stylesAuth.authHeader}>Login</h3>
      <form onSubmit={handleSubmit} className={styles.form}>
        {error && <p className={styles.error}>{error}</p>}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className={styles.input}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className={styles.input}
        />
        <button type="submit" className={styles.submitBtn}>
          Login
        </button>

        {onForgotPassword && (
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <button
              type="button"
              onClick={onForgotPassword}
              className={stylesAuth.toggleBtn}
              style={{ fontSize: "0.9rem" }}
            >
              Forgot your password?
            </button>
          </div>
        )}
      </form>
    </>
  );
};

export default LoginForm;
